import {
  Component,
  inject,
  OnDestroy,
  OnInit,
  output,
  signal,
  ViewChild,
  WritableSignal,
} from '@angular/core';
import { ModalBaseComponent } from '../../../shared/components/modal-base/modal-base.component';
import { Booking, RateType } from '../../../core/models/entities/booking';
import { ModalService } from '../../../core/services/modal.service';
import { Store } from '@ngrx/store';
import { ToastService } from 'angular/app/core/services/toast.service';
import { roomsActions } from 'angular/app/core/store/rooms';
import { bookingsActions } from 'angular/app/core/store/bookings';
import {
  BookingDurationType,
  bookingFormActions,
} from '../../../core/store/booking-form';
import { PublicBookingComponent } from '../../../shared/components/public-booking/public-booking.component';
import { formatISO, parseISO, startOfDay, subDays } from 'date-fns';
import { DatetimeHelper } from 'angular/app/core/helpers/datetime.helper';
import { fromStore } from 'angular/app/core/store';
import { firstValueFrom, Subject, takeUntil } from 'rxjs';
import { Invoice } from '../../../core/models/entities/invoice';
import { BookingsService } from '../../../core/services/bookings.service';
import { ApiGetSuccessResponse } from '../../../core/models/common/api-get-success-response';

@Component({
  selector: 'app-booking-edit-modal',
  template: `
    <app-modal [modalInstance]="modalInstance" (onOk)="onOk()">
      @if (loading()) {
        <app-spacer size="8"></app-spacer>
        <div class="text-center">
          <app-loader></app-loader>
        </div>
        <app-spacer size="8"></app-spacer>
      } @else if (booking()) {
        @if (infoMessage()) {
          <app-alert>
            <div [innerHtml]="infoMessage()"></div>
          </app-alert>
        }

        @if (showRecurrenceInfoMessage()) {
          <div [class.mt-4]="infoMessage()">
            <app-alert>
              <p>Vous modifiez une réservation publique récurrente.</p>
              <p>
                Les modifications apportées à cette réservation s'appliqueront à
                <span class="underline">toutes les réservations</span>.</p
              >
              <p
                >Si vous souhaitez modifier une date en particulier, vous devez
                annuler l'annuler et créer une nouvelle réservation.</p
              >
              <p
                >Aucun changement ne sera appliqué sur la facture, le panier
                ci-contre est à titre indicatif.</p
              >
            </app-alert>
          </div>
        }

        @if (!booking().automatic_invoicing_enabled) {
          <div [class.mt-4]="infoMessage() || showRecurrenceInfoMessage()">
            <app-manual-invoicing-warning
              [editMode]="true"
            ></app-manual-invoicing-warning>
          </div>
        }

        <app-public-booking
          #publicBookingComponent
          [originalBooking]="booking()"
          (bookingSaved)="saved($event)"
        ></app-public-booking>
      } @else if (error()) {
        <app-alert variant="danger">{{ error() }}</app-alert>
      }
    </app-modal>
  `,
  standalone: false,
})
export class PublicBookingEditModalComponent
  extends ModalBaseComponent
  implements OnInit, OnDestroy
{
  @ViewChild('publicBookingComponent')
  publicBookingComponent: PublicBookingComponent;
  booking: WritableSignal<Booking | null> = signal(null);
  loading = signal(false);
  error = signal<string | null>(null);
  bookingSaved = output<void>();
  room$ = this.store.select(fromStore.selectBookingFormRoom);
  onDestroy$ = new Subject<void>();
  bookingService = inject(BookingsService);
  infoMessage: WritableSignal<string | null> = signal(null);
  showRecurrenceInfoMessage: WritableSignal<boolean> = signal(false);

  constructor(
    private modalService: ModalService,
    private toastService: ToastService,
    private store: Store,
  ) {
    super();
  }

  async ngOnInit() {
    const focusedBooking: Booking = this.store.selectSignal(
      fromStore.selectFocusedBooking,
    )();
    if (focusedBooking.recurrence_rule) {
      this.loading.set(true);
      await this.setBookingWithFirstRecurrenceBooking(focusedBooking);
      if (this.booking()) {
        this.showRecurrenceInfoMessage.set(true);
        this.initForm();
        await this.loadRecurrenceExceptions();
      } else {
        this.error.set('Impossible de charger la réservation');
      }
      this.loading.set(false);
    } else {
      this.booking.set(focusedBooking);
      this.initForm();
    }

    this.modalInstance.config.update((config) => ({
      ...config,
      alignTop: true,
    }));
  }

  ngOnDestroy() {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  /**
   * On modifie toute la récurrence dans ce cas, du coup on se place à la 1re date de la récurrence
   * Pas trouvé de manière plus élégante de faire ça
   * @param booking
   */
  async setBookingWithFirstRecurrenceBooking(booking: Booking) {
    const response: ApiGetSuccessResponse<Booking[]> = await firstValueFrom(
      this.bookingService.findAll({ search: booking.reference }),
    );
    const bookings: Booking[] = response.data;
    const bookingsSortedByStartAsc = bookings
      .filter(
        (loopBooking: Booking) => loopBooking.group_id === booking.group_id,
      )
      .sort((a, b) => a.start.localeCompare(b.start));
    const bookingResponse: ApiGetSuccessResponse<Booking> =
      await firstValueFrom(
        this.bookingService.view(bookingsSortedByStartAsc[0].id),
      );
    this.booking.set(bookingResponse.data);
  }

  private normalizedStartEnd(): { start: string; end: string } {
    const booking = this.booking();
    if (booking.duration.rate !== RateType.Daily) {
      return { start: booking.start, end: booking.end };
    }
    const endsNextDay = booking.rooms.some((room) => room.max_end_next_day)
      ? 1
      : 0;
    return {
      start: formatISO(startOfDay(parseISO(booking.start))),
      end: formatISO(startOfDay(subDays(parseISO(booking.end), endsNextDay))),
    };
  }

  private async loadRecurrenceExceptions() {
    const booking = this.booking();
    if (!booking?.recurrence) return;

    const { start, end } = this.normalizedStartEnd();

    try {
      const exceptions =
        await this.bookingService.verifyDateExceptionsForRecurrence({
          room_id: booking.room_id,
          complementary_rooms_ids: (booking.complementary_rooms ?? []).map((r) => r.id),
          all_day: booking.duration.rate !== RateType.Hourly,
          start: DatetimeHelper.dateToApiFormat(parseISO(start), true),
          end: DatetimeHelper.dateToApiFormat(parseISO(end), true),
          is_public: true,
          recurrence: {
            ...booking.recurrence,
            end: booking.recurrence.end
              ? DatetimeHelper.dateToApiFormat(
                  parseISO(booking.recurrence.end),
                  true,
                )
              : booking.recurrence.end,
          },
          booking_id: booking.id,
        });
      this.store.dispatch(
        bookingFormActions.setData({ recurrence_exceptions: exceptions }),
      );
    } catch {
      // Les exceptions sont informatives, on ne bloque pas si l'appel échoue
    }
  }

  initForm() {
    this.modalInstance.config.update((config) => ({
      ...config,
      showActionButtons: false,
    }));
    this.store.dispatch(roomsActions.findOne({ id: this.booking().room.id }));
    const { start, end } = this.normalizedStartEnd();
    let recurrence_end = this.booking().recurrence_end;
    if (
      this.booking().duration.rate === RateType.Daily &&
      this.booking().recurrence_end
    ) {
      const endsNextDay = this.booking().rooms.some(
        (room) => room.max_end_next_day,
      )
        ? 1
        : 0;
      recurrence_end = formatISO(
        startOfDay(
          subDays(parseISO(this.booking().recurrence_end), endsNextDay),
        ),
      );
    }

    this.store.dispatch(
      bookingFormActions.setData({
        id: this.booking().id,
        status: this.booking().status,
        booking_type: this.booking().booking_type,
        room_id: this.booking().room_id,
        start,
        end,
        coupon_code:
          this.booking().invoices.find((invoice: Invoice) => {
            return invoice.coupon?.code;
          })?.coupon.code ?? null,
        room_amenities_ids:
          this.booking().group_room_amenities?.map((ra) => ra.id) || [],
        amenities_extras_ids:
          this.booking().group_amenities_extras?.map((ae) => ae.id) || [],
        complementary_rooms_ids: this.booking()
          .rooms.filter((room) => room.id !== this.booking().room_id)
          .map((room) => room.id),
        duration_type:
          this.booking().duration.rate === RateType.Hourly
            ? BookingDurationType.Hourly
            : this.booking().duration.quantity === 1
              ? BookingDurationType.FullDay
              : BookingDurationType.MultipleDays,
        recurrence: this.booking().recurrence
          ? {
              ...this.booking().recurrence,
              end: recurrence_end,
            }
          : null,
        accepts_terms: true,

        // Informations sur l'événement
        event_type_id: this.booking().event_type_id,
        attendance: this.booking().attendance,
        comment: this.booking().comment,
        rate_id: this.booking().rate_id,
        custom_fields: this.booking().custom_fields ?? [],

        // Coordonnées du client
        first_name: this.booking().first_name,
        last_name: this.booking().last_name,
        company: this.booking().company,
        address: this.booking().address,
        zip: this.booking().zip,
        city: this.booking().city,
        email: this.booking().email,
        phone: this.booking().phone,

        // Options de réservation
        booking_options_ids:
          this.booking().booking_options?.map((bo) => bo.id) ?? [],
      }),
    );

    this.store.dispatch(bookingFormActions.setStep({ step: 0 }));
    this.store.dispatch(bookingFormActions.setLatestCompletedStep({ step: 3 }));

    this.room$.pipe(takeUntil(this.onDestroy$)).subscribe((room) => {
      if (!room) return;

      const allRoomAmenitiesExtras = room.room_amenities.flatMap(
        (el) => el.amenity_extras,
      );
      const removedAmenities = [];

      for (const roomAmenity of this.booking().room_amenities.filter(
        (el) => !el.included,
      )) {
        if (!room.room_amenities.some((el) => el.id === roomAmenity.id)) {
          removedAmenities.push(roomAmenity.name ?? roomAmenity.amenity.name);
        }
      }

      for (const amenityExtra of this.booking().amenities_extras) {
        if (!allRoomAmenitiesExtras.some((el) => el.id === amenityExtra.id)) {
          removedAmenities.push(amenityExtra.name);
        }
      }

      if (removedAmenities.length) {
        this.infoMessage.set(`
            Les équipements : ${removedAmenities.join(', ')} ne sont plus disponibles et vont être retirés de cette réservation si vous enregistrez. <br />Si vous souhaitez ajouter de nouveaux équipements, vous pouvez les sélectionner depuis l'étape "Equipements".
        `);
      } else {
        this.infoMessage.set(null);
      }
    });
  }

  saved(booking: Booking) {
    this.store.dispatch(bookingsActions.setFocusedBooking({ booking }));
    this.bookingSaved.emit();
    this.modalService.close();
    this.toastService.success('Réservation modifiée : ' + this.booking().name);
  }

  onOk() {
    this.publicBookingComponent.submit();
  }
}
